/*
 * ls-stade-motion.js — shared runner for the Tous au stade scene motion (p70).
 *
 * The page CONTENT is a native aiconeo document (rendered into #aico-content by
 * content-builder-neo.js): every text, photo, video and link is an ordinary
 * element staff edit in the builder. This theme layer only animates it. It
 * never writes content and never builds content markup: a scene script finds
 * its native section, reads its structure (element types in document order)
 * and adds RUNTIME classes / inline transforms / GSAP ScrollTriggers.
 *
 *   sections/ls-stade-motion.liquid prints
 *     <script type="application/json" id="ls-stade-motion-config">
 *       {"hero": "<neo section uuid>", "carousel": "…", …}
 *     </script>
 *   and loads GSAP + ScrollTrigger + this file + one script per scene. Each
 *   scene script registers itself:
 *
 *     LsStadeMotion.scene('hero', function (section, ctx) { … return api; });
 *
 *   section — the `[data-neo-section="<uuid>"]` element (stamped here with
 *             data-ls-stade-scene="<name>", the stylesheet hook)
 *   ctx     — { reduced, mm: gsap.matchMedia(), q(sel), qa(sel), items(type) }
 *   api     — optional { refresh() } called after every scene ran.
 *
 * Scenes run ONCE, in document order (pinned ScrollTriggers must be created
 * top to bottom), after the Neo content painted: `ls:theme-binds-applied` is
 * dispatched at the end of every Neo hydrate and on every no-Neo path, and
 * `window.lsThemeBindsApplied` covers a runner that loads later. Then one
 * ScrollTrigger.refresh(). No config / no section / no GSAP → the native page
 * simply stays static, which is also the reduced-motion and no-JS state.
 */
(function () {
  'use strict';

  var registry = {};
  var started = false;

  var TYPE_SELECTORS = {
    heading: '.content-type-neo-heading',
    text: '.content-type-neo-text',
    button: '.content-type-neo-button',
    image: '.content-type-neo-image',
    video: '.content-type-neo-video',
    slider: '.neo-slider',
    columns: '.content-row',
    slot: '[data-ls-slot]'
  };

  function readConfig() {
    var node = document.getElementById('ls-stade-motion-config');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}') || null;
    } catch (err) {
      return null;
    }
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function makeCtx(section) {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return {
      reduced: reduced,
      mm: window.gsap && gsap.matchMedia ? gsap.matchMedia() : null,
      q: function (sel) { return section.querySelector(sel); },
      qa: function (sel) { return toArray(section.querySelectorAll(sel)); },
      /** Native elements of one type inside the section, document order. */
      items: function (type) {
        return toArray(section.querySelectorAll(TYPE_SELECTORS[type] || type));
      }
    };
  }

  function run() {
    if (started) return;
    started = true;
    var config = readConfig();
    if (!config || !window.gsap || !window.ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);

    var jobs = [];
    Object.keys(registry).forEach(function (name) {
      var id = String(config[name] || '').trim();
      if (!/^[A-Za-z0-9-]+$/.test(id)) return;
      var section = document.querySelector('#aico-content [data-neo-section="' + id + '"]');
      if (!section) return;
      section.setAttribute('data-ls-stade-scene', name);
      jobs.push({ name: name, section: section });
    });
    // Document order: pins must be created top to bottom.
    jobs.sort(function (a, b) {
      return a.section.compareDocumentPosition(b.section) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });

    var apis = [];
    jobs.forEach(function (job) {
      try {
        var api = registry[job.name](job.section, makeCtx(job.section));
        if (api) apis.push(api);
        job.section.classList.add('ls-stade-scene--ready');
      } catch (err) {
        if (window.console && console.warn) console.warn('ls-stade-motion: scene ' + job.name + ' failed', err);
      }
    });
    apis.forEach(function (api) {
      if (api && typeof api.refresh === 'function') {
        try { api.refresh(); } catch (e) { /* best effort */ }
      }
    });
    ScrollTrigger.refresh();
    // Web fonts and late images change heights under the pins.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
    window.addEventListener('load', function () { ScrollTrigger.refresh(); }, { once: true });
  }

  function whenReady() {
    if (window.lsThemeBindsApplied) return run();
    document.addEventListener('ls:theme-binds-applied', run, { once: true });
    // Net: a Neo render that never announces must not leave the runner idle.
    window.setTimeout(run, 6000);
  }

  window.LsStadeMotion = {
    scene: function (name, fn) {
      registry[name] = fn;
    },
    types: TYPE_SELECTORS
  };

  // This file is deferred and the scene scripts are deferred AFTER it, so
  // nothing may start before DOMContentLoaded (it fires once every deferred
  // script ran and registered its scene) — even when the inline Neo render
  // already set `lsThemeBindsApplied` (readyState is 'interactive' here).
  // `load` is the net for a runner injected after DOMContentLoaded.
  if (document.readyState === 'complete') {
    window.setTimeout(whenReady, 0);
  } else {
    document.addEventListener('DOMContentLoaded', whenReady);
    window.addEventListener('load', whenReady);
  }
})();
